/**
 * Onboarding kit primitives — React/TS ports of the design's ob-base.jsx
 * (`Btn`, `Badge`, `Switch`, `Seg`, `Field`, `Check`, `Modal`, `LangToggle`,
 * `TopBar`), ob-admin.jsx's `Ring`, ob-wizard.jsx's `StepHead` and the
 * `.ob-note` callout. Every screen renders inside <ObShell>, which owns the
 * `.ob` root the stylesheet is scoped to.
 */
import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CircleAlert, Hourglass, Info, Languages, LoaderCircle, X, Check as CheckIcon } from "lucide-react";
import { Ic, type LucideIcon } from "./icons";
import { currentObLocale, setObLocale } from "./locale";
import "./onboarding.css";

/* ------------------------------------------------------------------ */
/* ObShell                                                             */
/* ------------------------------------------------------------------ */

export interface ObShellProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Admin-console variant (`.ob.ob-adm` — no page padding, for the sidebar layout). */
  admin?: boolean;
  children?: React.ReactNode;
}

/**
 * The `.ob` root: design tokens, the glass background, and `lang`/`dir`
 * taken from the active i18n language (so `:lang(ar)` switches to IBM Plex
 * Sans Arabic and every `.ob[dir=rtl]` rule applies).
 */
export function ObShell({ admin, className = "", children, ...rest }: ObShellProps) {
  const { i18n } = useTranslation();
  const lang = currentObLocale(i18n.language);
  return (
    <div
      {...rest}
      lang={lang}
      dir={lang === "ar" ? "rtl" : "ltr"}
      className={`ob${admin ? " ob-adm" : ""}${className ? ` ${className}` : ""}`}
    >
      {children}
    </div>
  );
}

/** A centred content column — the design's entry-screen `Shell` (`.ob-wrap`). */
export function ObMain({
  width = 1200,
  className = "",
  style,
  children,
  ...rest
}: React.HTMLAttributes<HTMLElement> & { width?: number }) {
  return (
    <main {...rest} className={`ob-wrap ob-anim${className ? ` ${className}` : ""}`} style={{ maxWidth: width, marginTop: 24, ...style }}>
      {children}
    </main>
  );
}

/* ------------------------------------------------------------------ */
/* Btn                                                                 */
/* ------------------------------------------------------------------ */

export type BtnKind = "pri" | "sec" | "ter";
export type BtnSize = "l" | "m" | "s";

export interface BtnProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  kind?: BtnKind;
  size?: BtnSize;
  /** Leading lucide icon. */
  icon?: LucideIcon | null;
  /** Trailing lucide icon. */
  iconEnd?: LucideIcon | null;
  /** Mirror the trailing icon in RTL (arrows). */
  flipEnd?: boolean;
  /** Show a spinner in place of the leading icon and disable the button. */
  busy?: boolean;
  children?: React.ReactNode;
}

export const Btn = React.forwardRef<HTMLButtonElement, BtnProps>(function Btn(
  { kind = "pri", size = "m", icon, iconEnd, flipEnd, busy, children, className = "", type = "button", disabled, ...rest },
  ref,
) {
  const iconOnly = children === undefined || children === null || children === false;
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={`ob-btn ob-${kind} ob-${size}${iconOnly ? " ob-ico" : ""}${className ? ` ${className}` : ""}`}
      {...rest}
    >
      {busy ? <Ic icon={LoaderCircle} className="ob-spin" /> : icon ? <Ic icon={icon} /> : null}
      {children}
      {iconEnd && <Ic icon={iconEnd} flip={flipEnd} />}
    </button>
  );
});

/* ------------------------------------------------------------------ */
/* Badge                                                               */
/* ------------------------------------------------------------------ */

export type BadgeKind = "ok" | "info" | "warn" | "bad" | "neu";

export function Badge({
  kind = "neu",
  icon,
  children,
  className = "",
}: {
  kind?: BadgeKind;
  icon?: LucideIcon;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <span className={`ob-badge b-${kind}${className ? ` ${className}` : ""}`}>
      {icon && <Ic icon={icon} weight="bold" />}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Switch                                                              */
/* ------------------------------------------------------------------ */

export function Switch({
  on,
  onChange,
  label,
  disabled,
  id,
}: {
  on: boolean;
  onChange: (next: boolean) => void;
  /** Accessible name (the design labels switches by aria-label). */
  label: string;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={!!on}
      aria-label={label}
      disabled={disabled}
      className="ob-sw"
      onClick={() => onChange(!on)}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Seg (segmented radio group)                                         */
/* ------------------------------------------------------------------ */

export interface SegOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

export function Seg<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: ReadonlyArray<SegOption<T>>;
  onChange: (next: T) => void;
  label: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const move = (from: number, delta: number) => {
    const n = options.length;
    const next = (from + delta + n) % n;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };
  return (
    <div role="radiogroup" aria-label={label} className="ob-seg">
      {options.map((o, i) => {
        const checked = value === o.value;
        return (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked || (!options.some((x) => x.value === value) && i === 0) ? 0 : -1}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => {
              // Arrow keys move the selection; in RTL the visual order flips.
              const rtl = (e.currentTarget.closest("[dir]") as HTMLElement | null)?.dir === "rtl";
              if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                e.preventDefault();
                move(i, e.key === "ArrowRight" && rtl ? -1 : 1);
              } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault();
                move(i, e.key === "ArrowLeft" && rtl ? 1 : -1);
              }
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Field                                                               */
/* ------------------------------------------------------------------ */

export function Field({
  label,
  hint,
  err,
  children,
  style,
  className = "",
  as = "label",
}: {
  label: React.ReactNode;
  hint?: React.ReactNode;
  err?: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  /** Use "div" when the field holds several inputs (e.g. <Otp>). */
  as?: "label" | "div";
}) {
  const Tag = as;
  return (
    <Tag className={`ob-label${className ? ` ${className}` : ""}`} style={style}>
      <span>{label}</span>
      {children}
      {err ? (
        <span className="ob-err" role="alert">
          <Ic icon={CircleAlert} weight="bold" />
          {err}
        </span>
      ) : hint ? (
        <span className="ob-hint">{hint}</span>
      ) : null}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* Check (progress state dot)                                          */
/* ------------------------------------------------------------------ */

export type CheckState = "done" | "run" | "wait";

export function Check({ state }: { state: CheckState }) {
  if (state === "done")
    return (
      <span className="ob-check ok" data-state="done">
        <Ic icon={CheckIcon} weight="bold" />
      </span>
    );
  if (state === "run")
    return (
      <span className="ob-check run" data-state="run">
        <Ic icon={LoaderCircle} weight="bold" size={18} className="ob-spin" />
      </span>
    );
  return <span className="ob-check wait" data-state="wait" />;
}

/* ------------------------------------------------------------------ */
/* Modal                                                               */
/* ------------------------------------------------------------------ */

/**
 * Dialog on a blurred scrim. Closes on Escape and on a click on the scrim
 * itself. Portaled to <body> inside its own `.ob.ob-layer` root (same
 * lang/dir), because a fixed element inside a `.ob-glass` card would be
 * trapped by that card's backdrop-filter.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  width,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  /** Accessible name (used as aria-label unless `labelledBy` is given). */
  title: string;
  children: React.ReactNode;
  width?: number;
  /** id of a heading inside the dialog to use as its accessible name. */
  labelledBy?: string;
}) {
  const { t, i18n } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  const lang = currentObLocale(i18n.language);
  return createPortal(
    <div className="ob ob-layer" lang={lang} dir={lang === "ar" ? "rtl" : "ltr"}>
      <div
        className="ob-scrim"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        data-testid="ob-modal-scrim"
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={labelledBy ? undefined : title}
          aria-labelledby={labelledBy}
          tabIndex={-1}
          className="ob-dialog"
          style={width ? { maxWidth: width } : undefined}
        >
          <Btn kind="ter" size="s" icon={X} className="ob-x" aria-label={t("onboarding.kit.close", "Close")} onClick={onClose} />
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/* Ring (setup-guide progress)                                         */
/* ------------------------------------------------------------------ */

export function Ring({ value, total, size = 76 }: { value: number; total: number; size?: number }) {
  const { t } = useTranslation();
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const p = total ? Math.min(1, Math.max(0, value / total)) : 0;
  return (
    <span
      style={{ position: "relative", width: size, height: size, flex: "none", display: "grid", placeItems: "center" }}
      role="img"
      aria-label={t("onboarding.kit.ring.label", { defaultValue: "{{value}} of {{total}} done", value, total })}
    >
      <svg width={size} height={size} style={{ position: "absolute", inset: 0, transform: "rotate(-90deg)" }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(148,163,184,.3)" strokeWidth="8" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#2B5BF5"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - p)}
          style={{ transition: "stroke-dashoffset .5s" }}
        />
      </svg>
      <span style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)" }} dir="ltr">
        {value}/{total}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* StepHead                                                            */
/* ------------------------------------------------------------------ */

export function StepHead({
  eyebrow,
  title,
  sub,
  id,
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  /** id for the <h1>, e.g. to label a section. */
  id?: string;
}) {
  return (
    <header style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
      {eyebrow && <p className="ob-eye">{eyebrow}</p>}
      <h1 id={id} className="ob-h2" style={{ fontSize: "clamp(22px,3cqi,28px)" }}>
        {title}
      </h1>
      {sub && <p className="ob-p">{sub}</p>}
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* Note (.ob-note callout)                                             */
/* ------------------------------------------------------------------ */

export type NoteKind = "warn" | "bad" | "info";

const NOTE_ICON: Record<NoteKind, LucideIcon> = { warn: Hourglass, bad: CircleAlert, info: Info };

export function Note({
  kind = "info",
  icon,
  title,
  children,
  action,
}: {
  kind?: NoteKind;
  /** Defaults per kind: warn → hourglass, bad → alert, info → info. */
  icon?: LucideIcon;
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Trailing action, e.g. a small <Btn>. */
  action?: React.ReactNode;
}) {
  return (
    <div className={`ob-note ${kind}`} role={kind === "info" ? "status" : "alert"}>
      <Ic icon={icon ?? NOTE_ICON[kind]} size={20} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <p style={{ margin: 0, fontWeight: 600 }}>{title}</p>}
        {children && <div style={{ margin: title ? "2px 0 0" : 0, fontSize: 13.5 }}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* LangToggle + TopBar                                                 */
/* ------------------------------------------------------------------ */

/** EN ⇄ ع toggle; switches the whole shell's locale (persisted in `weco-locale`). */
export function LangToggle() {
  const { t, i18n } = useTranslation();
  const lang = currentObLocale(i18n.language);
  const next = lang === "ar" ? "en" : "ar";
  return (
    <Btn
      kind="ter"
      size="m"
      icon={Languages}
      onClick={() => setObLocale(next, i18n)}
      aria-label={t("onboarding.kit.lang.switch", "Switch to Arabic")}
    >
      {/* The target language's own name, in that language — never translated. */}
      <span lang={next}>
        <span className="ob-hide-sm">{lang === "ar" ? "English" : "العربية"}</span>
        <span className="ob-show-sm">{lang === "ar" ? "EN" : "ع"}</span>
      </span>
    </Btn>
  );
}

export function TopBar({
  orgName,
  mid,
  right,
  hideLangToggle,
}: {
  /** Shown after the product name; on narrow screens it replaces it. */
  orgName?: string | null;
  /** Extra inline content after the org name (e.g. a badge). */
  mid?: React.ReactNode;
  /** Actions before the language toggle. */
  right?: React.ReactNode;
  hideLangToggle?: boolean;
}) {
  const hasOrg = !!orgName;
  return (
    <header className="ob-top">
      <div className="ob-top-in">
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, marginInlineEnd: "auto" }}>
          <span className="ob-logo" aria-hidden="true">
            W
          </span>
          <span
            style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-.01em", color: "var(--ink)", whiteSpace: "nowrap" }}
            className={hasOrg ? "ob-hide-sm" : ""}
          >
            We‑Co Suite
          </span>
          {hasOrg && (
            <>
              <span className="ob-div ob-hide-sm" />
              <span
                style={{
                  fontSize: 14.5,
                  fontWeight: 600,
                  color: "var(--ink)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                  flex: "0 1 auto",
                }}
              >
                {orgName}
              </span>
            </>
          )}
          {mid && <span style={{ flex: "none", display: "inline-flex" }}>{mid}</span>}
        </div>
        {right}
        {!hideLangToggle && <LangToggle />}
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */

/** `useId` wrapper that returns a CSS-safe id (React's ids contain colons). */
export function useObId(prefix = "ob"): string {
  const raw = useId();
  return `${prefix}-${raw.replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

